import { clearActiveSellerGroupId } from '@seller-hermes/features/portal/portal-local-context';
import { Auth } from 'aws-amplify';
import { Loader2 } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { track } from './telemetry';

type AuthState = 'checking' | 'authenticated' | 'unauthenticated';

// Tracks the last authenticated Cognito `sub` seen in this browser so an
// identity change can be detected even when a cached/SSO session skips the
// LoginPage entirely (its mount effect is otherwise the only place the
// shared active-seller-group cookie gets cleared) — LOG-475, Gap 2.
const LAST_SEEN_AUTH_SUB_KEY = 'abbiamo.lastSeenAuthSub';

interface RequireAuthProps {
  children: React.ReactNode;
}

export const RequireAuth = ({ children }: RequireAuthProps) => {
  const navigate = useNavigate();
  const [authState, setAuthState] = React.useState<AuthState>('checking');

  React.useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((user: { attributes?: { sub?: string } }) => {
        const sub = user?.attributes?.sub;
        const lastSeenSub = window.localStorage.getItem(
          LAST_SEEN_AUTH_SUB_KEY,
        );

        if (sub && sub !== lastSeenSub) {
          clearActiveSellerGroupId();
          window.localStorage.setItem(LAST_SEEN_AUTH_SUB_KEY, sub);
        }

        track({ name: 'portal_auth_check', result: 'authenticated' });
        setAuthState('authenticated');
      })
      .catch(() => {
        track({ name: 'portal_auth_check', result: 'unauthenticated' });
        setAuthState('unauthenticated');
        navigate('/login', { replace: true });
      });
  }, [navigate]);

  if (authState === 'checking') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Loader2 className="animate-spin" size={28} aria-hidden />
      </div>
    );
  }

  if (authState === 'unauthenticated') {
    return null;
  }

  return <>{children}</>;
};
